// BULK "SET FIELDS" ON THE ISSUE LIST - the form, and what it sends.
//
// Moved out of pages/Issue/IssuePage.jsx on 2026-10-15 when it gained the
// tags and the contractor (owner: "add the tags in set fields for bulk
// issues, and the dicipline, should list each contractor by discipline", and
// the same day "issues, can be tagged by multiple tags"), so the form and
// its payload can be tested without mounting the whole issue manager.
//
// THE RULE IS THE ONE IT ALWAYS HAD: a field left untouched is left alone on
// every issue; the "clear" box beside a field empties it. Tags and
// activities add to, take from, or replace each issue's list - add and
// remove keep every one nobody named. The discipline picker lists each contractor under its discipline;
// picking one fills the Contractor row too, which can also be set alone.
import React from "react";
import { Select, Input, DatePicker, Segmented } from "antd";
import DisciplineContractorPicker from "components/Issue/DisciplineContractorPicker";
import LocationPicker from "components/Coordination/LocationPicker";
import { leafActivityOptions, LEAF_SELECT_PROPS } from "helpers/schedule/leafActivityOptions";
import { T } from "pages/Issue/theme";
import { matchOption } from "components/SearchSelect/searchSelect";

export const EMPTY_BULK_FIELDS = Object.freeze({
  pick: null, company: undefined, tagMode: "add", tagList: [], actMode: "add", actList: [],
  area: "", level: "", grid: "", priority: undefined, dueDate: null, clear: Object.freeze({}),
});

/** The `value` POST /issue/bulk-actions { action: "fields" } takes; {} when nothing was set. */
export function bulkFieldsPayload(f) {
  const value = {};
  if (f.pick) value.discipline = f.pick.discipline;
  if (f.clear.responsibleCompanyId) value.responsibleCompanyId = ""; else if (f.company) value.responsibleCompanyId = f.company;
  const tagList = (f.tagList || []).map((t) => String(t).trim().toUpperCase()).filter(Boolean);
  if (f.clear.tags) value.tags = [];
  else if (tagList.length) value[f.tagMode === "remove" ? "removeTags" : (f.tagMode === "replace" ? "tags" : "addTags")] = tagList;
  // SEVERAL ACTIVITIES, like tags (owner, 2026-10-17): add keeps the
  // activities each issue already has, remove takes the picked ones off,
  // replace sets exactly the picked list.
  const actList = (f.actList || []).filter(Boolean);
  if (f.clear.ganttItemId) value.ganttItemId = "";
  else if (actList.length) value[f.actMode === "remove" ? "removeGanttItemIds" : (f.actMode === "replace" ? "ganttItemIds" : "addGanttItemIds")] = actList;
  for (const k of ["area", "level", "grid"]) { if (f.clear[k]) value[k] = ""; else if (String(f[k] || "").trim()) value[k] = String(f[k]).trim(); }
  if (f.priority !== undefined) value.priority = f.priority;
  if (f.clear.dueDate) value.dueDate = ""; else if (f.dueDate) value.dueDate = f.dueDate.toISOString();
  return value;
}

export default function BulkSetFieldsForm({
  bulkFields, setBulkFields, projectFilter, disciplines = [], roster = [], tags = [], activities = [],
}) {
  const row = (label, control, clearKey) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      <div style={{ width: 90, fontSize: 12, color: T.muted }}>{label}</div>
      <div style={{ flex: 1 }}>{control}</div>
      {clearKey && (
        <label style={{ fontSize: 11.5, whiteSpace: "nowrap", cursor: "pointer" }} title="Empty this field on every issue">
          <input type="checkbox" checked={!!bulkFields.clear[clearKey]} onChange={(e) => setBulkFields((f) => ({ ...f, clear: { ...f.clear, [clearKey]: e.target.checked } }))} style={{ marginRight: 4 }} />clear
        </label>
      )}
    </div>
  );
  const setF = (patch) => setBulkFields((f) => ({ ...f, ...patch }));
  return (
    <>
      {row("Tags", (
        <div style={{ display: "flex", gap: 6 }}>
          <Segmented size="small" value={bulkFields.tagMode} disabled={!!bulkFields.clear.tags}
            options={[{ value: "add", label: "Add" }, { value: "remove", label: "Remove" }, { value: "replace", label: "Replace" }]}
            onChange={(v) => setF({ tagMode: v })} />
          <Select mode="tags" allowClear style={{ flex: 1, minWidth: 0 }} aria-label="Tags" maxTagCount="responsive"
            disabled={!!bulkFields.clear.tags} value={bulkFields.tagList}
            placeholder={bulkFields.tagMode === "remove" ? "Tags to take off" : "Leave unchanged - or pick / type tags, e.g. SNAG"}
            options={tags.map((t) => ({ value: t.tag, label: `${t.tag}  ·  ${t.count} issue${t.count === 1 ? "" : "s"}` }))}
            tokenSeparators={[","]}
            notFoundContent={projectFilter ? "No tags on this project yet - type to start one" : "Type a tag, or pick a project to see its tags"}
            onChange={(v) => setF({ tagList: (v || []).map((t) => String(t).toUpperCase()) })} />
        </div>
      ), "tags")}
      {bulkFields.tagMode === "replace" && !bulkFields.clear.tags && (bulkFields.tagList || []).length ? (
        <div style={{ fontSize: 11.5, color: T.muted, margin: "-4px 0 10px 100px" }}>Replace takes every other tag off these issues.</div>
      ) : null}
      {row("Discipline", <DisciplineContractorPicker allowNone placeholder="Leave unchanged"
        disciplines={disciplines} roster={roster}
        noRosterReason={projectFilter ? "" : "Pick a project in the toolbar to list its contractors under each discipline"}
        value={bulkFields.pick ? { discipline: bulkFields.pick.discipline, responsibleCompanyId: bulkFields.company } : null}
        onChange={(pick) => setBulkFields((f) => ({ ...f, pick, company: pick && pick.responsibleCompanyId !== undefined ? pick.responsibleCompanyId : f.company }))} />)}
      {row("Contractor", <Select filterOption={matchOption} allowClear showSearch optionFilterProp="label" style={{ width: "100%" }} aria-label="Contractor"
        disabled={!projectFilter || !!bulkFields.clear.responsibleCompanyId}
        placeholder={projectFilter ? "Leave unchanged - or pick a contractor under its discipline above" : "Pick a project in the toolbar to choose a contractor"}
        value={bulkFields.company} options={roster.map((g) => ({ value: String(g.companyId), label: g.role === "owner" ? `${g.companyName || "Owner"} (owner)` : (g.label || g.companyName) }))}
        onChange={(v) => setF({ company: v })} />, "responsibleCompanyId")}
      {/* THE LOWEST ACTIVITY ONLY, its path beneath (owner, 2026-10-15). */}
      {row("Activities", (
        <div style={{ display: "flex", gap: 6 }}>
          <Segmented size="small" value={bulkFields.actMode} disabled={!projectFilter || !!bulkFields.clear.ganttItemId}
            options={[{ value: "add", label: "Add" }, { value: "remove", label: "Remove" }, { value: "replace", label: "Replace" }]}
            onChange={(v) => setF({ actMode: v })} />
          <Select filterOption={matchOption} mode="multiple" allowClear {...LEAF_SELECT_PROPS} style={{ flex: 1, minWidth: 0 }} aria-label="Activities" maxTagCount="responsive"
            disabled={!projectFilter || !!bulkFields.clear.ganttItemId}
            placeholder={projectFilter ? (bulkFields.actMode === "remove" ? "Activities to take off" : "Leave unchanged - or pick one or several") : "Pick a project in the toolbar to choose activities"}
            value={bulkFields.actList}
            options={leafActivityOptions(activities, { offer: (it) => bulkFields.actMode === "remove" || !it.disabled })}
            onChange={(v) => setF({ actList: v || [] })} />
        </div>
      ), "ganttItemId")}
      {bulkFields.actMode === "replace" && !bulkFields.clear.ganttItemId && (bulkFields.actList || []).length ? (
        <div style={{ fontSize: 11.5, color: T.muted, margin: "-4px 0 10px 100px" }}>Replace takes every other activity off these issues.</div>
      ) : null}
      {row("Area", <Input placeholder="Leave unchanged" value={bulkFields.area} disabled={!!bulkFields.clear.area} onChange={(e) => setF({ area: e.target.value })} />, "area")}
      {row("Level", <Input placeholder="Leave unchanged" value={bulkFields.level} disabled={!!bulkFields.clear.level} onChange={(e) => setF({ level: e.target.value })} />, "level")}
      {/* The grid tag picker the issue page and New issue use (owner, 2026-10-19):
          several locations, either axis first, the project's mapped grid offered. */}
      {row("Grid", <LocationPicker projectId={projectFilter || undefined} fields={["grid"]} fill allTags
        value={{ grid: bulkFields.grid }} disabled={!!bulkFields.clear.grid}
        gridPlaceholder="Leave unchanged - or one or several, e.g. I-40, L-50"
        onChange={(v) => setF({ grid: v.grid || "" })} />, "grid")}
      {row("Priority", <Select allowClear style={{ width: "100%" }} placeholder="Leave unchanged" value={bulkFields.priority}
        options={[{ value: "urgent", label: "Urgent" }, { value: "normal", label: "Normal" }, { value: "low", label: "Low" }]} onChange={(v) => setF({ priority: v })} />)}
      {row("Due date", <DatePicker style={{ width: "100%" }} placeholder="Leave unchanged" value={bulkFields.dueDate} disabled={!!bulkFields.clear.dueDate} onChange={(d) => setF({ dueDate: d })} />, "dueDate")}
    </>
  );
}
